import { IconArrowRight, IconCheck, IconEye, IconLock, IconPlayerPlay } from "@tabler/icons-react";
import { Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";

import { useLocalize } from "../../content/useLocalize";
import { baseRewardForDifficulty } from "../../engine/progression";
import { contractTypeText } from "../../i18n/enumText";
import { difficultyTooltip, rewardTooltip } from "../../i18n/tooltips";
import type { Client, Contract } from "../../types";
import buttonStyles from "../common/Button.module.css";
import { Card } from "../common/Card";
import { DifficultyMeter } from "../common/DifficultyMeter";
import { Tooltip } from "../common/Tooltip";
import { formatMoney, formatNumber } from "../common/format";
import { activateOnKey } from "../common/keyActivate";
import { cx } from "../common/cx";
import { ContractRiskBadge } from "./ContractRiskBadge";
import { RewardGradeHint } from "./RewardGradeHint";
import styles from "./ContractCard.module.css";

export interface ContractCardProps {
  contract: Contract;
  client?: Client;
  unlocked: boolean;
  completed: boolean;
  reputation: number;
  recommended?: boolean;
  onOpen: () => void;
}

export function ContractCard({
  contract,
  client,
  unlocked,
  completed,
  reputation,
  recommended = false,
  onOpen,
}: ContractCardProps): JSX.Element {
  const { i18n } = useLingui();
  const t = useLocalize();
  const reward = contract.reward ?? baseRewardForDifficulty(contract.difficulty);
  const interactive = unlocked || completed;
  const requiredRep = contract.requiredReputation;

  return (
    <Card
      interactive
      selected={recommended}
      className={cx(styles.card, !interactive && styles.locked)}
      onClick={onOpen}
      onKeyDown={activateOnKey(onOpen)}
      role="button"
      tabIndex={0}
    >
      <div className={styles.head}>
        <h3 className={styles.title}>{t(contract.title)}</h3>
        {client && <span className={styles.client}>{t(client.name)}</span>}
      </div>

      <div className={styles.meta}>
        <Tooltip content={i18n._(difficultyTooltip())} tabIndex={-1}>
          <DifficultyMeter difficulty={contract.difficulty} />
        </Tooltip>
        <span className={styles.taskTag}>{i18n._(contractTypeText(contract.primaryTask))}</span>
        {contract.riskLevel && <ContractRiskBadge riskLevel={contract.riskLevel} />}
      </div>

      <div className={styles.stats}>
        <Tooltip content={i18n._(rewardTooltip())} tabIndex={-1}>
          <div className={styles.stat}>
            <span className={styles.statLabel}>
              <Trans>Награда</Trans>
            </span>
            <span className={cx(styles.statValue, styles.money, "mono")}>
              {formatMoney(reward.money)}
            </span>
          </div>
        </Tooltip>
        <Tooltip content={i18n._(rewardTooltip())} tabIndex={-1}>
          <div className={styles.stat}>
            <span className={styles.statLabel}>
              <Trans>Опыт</Trans>
            </span>
            <span className={cx(styles.statValue, "mono")}>
              +{formatNumber(reward.xp)} <Trans>XP</Trans>
            </span>
          </div>
        </Tooltip>
      </div>

      <RewardGradeHint />

      <div className={styles.foot}>
        {completed ? (
          <span className={styles.done}>
            <IconCheck size={16} aria-hidden="true" />
            <Trans>Завершён</Trans>
          </span>
        ) : unlocked ? (
          <span className={cx(buttonStyles.button, buttonStyles.primary)} aria-hidden="true">
            <span className={buttonStyles.icon}>
              {recommended ? <IconPlayerPlay size={17} /> : <IconArrowRight size={17} />}
            </span>
            {recommended ? <Trans>Взять контракт</Trans> : <Trans>Открыть</Trans>}
          </span>
        ) : (
          <div className={styles.lockRow}>
            <span className={styles.lockReason}>
              <IconLock size={15} aria-hidden="true" />
              <Trans>
                Нужна репутация {requiredRep} · у тебя {reputation}
              </Trans>
            </span>
            <span
              className={cx(buttonStyles.button, buttonStyles.secondary, buttonStyles.small)}
              aria-hidden="true"
            >
              <span className={buttonStyles.icon}>
                <IconEye size={16} />
              </span>
              <Trans>Предпросмотр</Trans>
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}
